import { AnyID } from '@types'
import {
  buildApproachMatrixForArm,
  decomposeMatrixPosition,
  getArmSideSign,
  resolveLayoutWidth,
  syncArmRotationToParent,
} from '@utils/linkedArms/armPlacement'
import { isRelaxIntegratedUnitByDefaults } from '@utils/atemporal/relaxArmVariants'
import {
  ARM_CATALOG_RE,
  clearArmFromUnitSnappedSides,
  unitCanHostIndependentArm,
} from '@utils/linkedArms/armOccupancy'
import { createArmRowContext } from '@utils/linkedArms/armRowContext'
import { resolveExteriorLipForArm } from '@utils/linkedArms/resolveExteriorArmPlacement'
import {
  isModularCornerSnapCatalog,
  isOreja,
  isRinconeroCatalog,
} from '@utils/sceneObjects/items/rinconeroUtils'
import { Matrix4, Quaternion, Vector3 } from 'three'
const REALIGN_DEBUG = false

const logRealign = (step: string, payload?: Record<string, unknown>) => {
  if (!REALIGN_DEBUG) return
  console.warn('[ArmRealignDebug]', step, payload ?? {})
}

const formatVec = (value: Vector3) => ({
  x: Number(value.x.toFixed(2)),
  y: Number(value.y.toFixed(2)),
  z: Number(value.z.toFixed(2)),
})

const summarizeUnit = (unit: SceneItem | undefined) => {
  if (!unit) return null
  const morph = (unit as any)?.morphs?.find(
    (entry: any) => entry?.name === 'Ancho' || entry?.name === 'width',
  )
  return {
    id: unit.id,
    catalogID: unit.catalogID,
    widthRaw: unit.width,
    widthResolvedMm: resolveLayoutWidth(unit),
    morphDefault: morph?.default ?? morph?._default ?? null,
    thickness: unit.thickness,
    position: formatVec(getWorldPosFromItem(unit)),
    firstParentID: unit.firstParentID ?? null,
  }
}

const summarizeArm = (arm: SceneItem | undefined, parent?: SceneItem) => {
  if (!arm) return null
  return {
    id: arm.id,
    catalogID: arm.catalogID,
    widthResolvedMm: resolveLayoutWidth(arm),
    position: formatVec(getWorldPosFromItem(arm)),
    firstParentID: arm.firstParentID ?? null,
    parentCatalogID: parent?.catalogID ?? null,
    parentSideSign: parent?.matrix ? getArmSideSign(parent, arm) : null,
  }
}

type SceneItem = {
  id: AnyID
  type?: string
  catalogID?: string
  matrix?: Matrix4
  firstParentID?: AnyID
  width?: number
  thickness?: number
  depth?: number
  icono?: string
  x?: number
  y?: number
  z?: number
}

type RealignContext = {
  getItem: (id: AnyID) => SceneItem | undefined
  listItems: () => Iterable<SceneItem>
  editItem: (id: AnyID, data: Record<string, unknown>) => void
  collideItems: (
    itemId: AnyID,
    otherId: AnyID,
    options?: { forcedSide?: 'left' | 'right' },
  ) => void
  replaceItem: (id: string, catalog: string, icono: string, type: string) => Promise<AnyID>
  removeItem?: (id: AnyID) => void
}

const isArmLinkedItem = (item: SceneItem | undefined) => {
  if (!item || item.type !== 'linkedItem') return false
  return ARM_CATALOG_RE.test(String(item.catalogID || ''))
}

const isBaseLinkedUnit = (item: SceneItem | undefined) => {
  if (!item || item.type !== 'linkedItem') return false
  const catalog = String(item.catalogID || '').toUpperCase()
  if (ARM_CATALOG_RE.test(catalog)) return false
  if (catalog.includes('ASIENTO') || catalog.includes('COJIN')) return false
  return true
}

const getWorldPosFromItem = (sceneItem: SceneItem) => {
  if (sceneItem.matrix) {
    const pos = new Vector3()
    sceneItem.matrix.decompose(pos, new Quaternion(), new Vector3())
    return pos
  }
  return new Vector3(Number(sceneItem.x || 0), Number(sceneItem.y || 0), Number(sceneItem.z || 0))
}

/** Lado flush del brazo (evita dot≈0 → derecha errónea en labio izquierdo). */
const resolveArmFlushSide = (parent: SceneItem, arm: SceneItem): 1 | -1 => {
  if (!parent.matrix || !arm.matrix) return getArmSideSign(parent, arm)
  const parentPos = getWorldPosFromItem(parent)
  const parentQuat = new Quaternion()
  parent.matrix.decompose(new Vector3(), parentQuat, new Vector3())
  const parentX = new Vector3(1, 0, 0).applyQuaternion(parentQuat)
  const armPos = getWorldPosFromItem(arm)
  const proj = armPos.clone().sub(parentPos).dot(parentX)
  const parentW = resolveLayoutWidth(parent) || Number(parent.width || 0)
  const armW = resolveLayoutWidth(arm) || Number(arm.width || 0)
  return Math.abs(proj - parentW) <= Math.abs(proj - -armW) ? 1 : -1
}

/**
 * BR atrapado entre su padre y la unidad nueva (Corner/RELAX/…).
 * Si la adyacencia no marca el labio como ocupado (gap del brazo), igual se purga.
 */
const isArmSandwichedBetweenParentAndNewUnit = (
  arm: SceneItem,
  parent: SceneItem,
  newUnit: SceneItem,
): boolean => {
  if (!arm.matrix || !parent.matrix || !newUnit.matrix) return false
  const parentPos = getWorldPosFromItem(parent)
  const parentQuat = new Quaternion()
  parent.matrix.decompose(new Vector3(), parentQuat, new Vector3())
  const parentX = new Vector3(1, 0, 0).applyQuaternion(parentQuat)
  const parentZ = new Vector3(0, 0, 1).applyQuaternion(parentQuat)
  const parentW = resolveLayoutWidth(parent) || Number(parent.width || 0)

  const newPos = getWorldPosFromItem(newUnit)
  const armPos = getWorldPosFromItem(arm)
  const newDelta = newPos.clone().sub(parentPos)
  if (Math.abs(newDelta.dot(parentZ)) > 420) return false

  const newT = newDelta.dot(parentX)
  const armT = armPos.clone().sub(parentPos).dot(parentX)
  const newW = resolveLayoutWidth(newUnit) || Number(newUnit.width || 0)

  // Vecino a la derecha del padre
  if (Math.abs(newT - parentW) <= 200 || (newT > 0 && newT < parentW + newW + 200)) {
    const lo = Math.min(0, newT) - 40
    const hi = Math.max(parentW, newT + newW) + 40
    if (armT >= lo && armT <= hi && armT > 40 && armT < parentW + newW) {
      // Brazo en la franja entre cara derecha del padre y el nuevo módulo
      if (armT >= parentW - 80 || (armT > parentW * 0.35 && newT >= parentW - 200)) return true
    }
  }

  // Vecino a la izquierda del padre
  if (newT < 0) {
    const newEnd = newT + newW
    const lo = Math.min(newT, 0) - 40
    const hi = 80
    if (armT >= lo && armT <= hi) return true
  }

  // Proximidad directa al origen de la unidad nueva
  const distToNew = Math.hypot(armPos.x - newPos.x, armPos.z - newPos.z)
  const armW = resolveLayoutWidth(arm) || Number(arm.width || 0)
  if (distToNew <= Math.max(armW, 200) + 120) return true

  return false
}

const resolveArmCatalogByParentDepth = (baseArmCatalog: string, parentItem: SceneItem) => {
  const baseMatch = String(baseArmCatalog || '')
    .trim()
    .toUpperCase()
    .match(/^BR\d+/)
  if (!baseMatch) return baseArmCatalog

  const baseCode = baseMatch[0]
  const rawDepthCandidate =
    (parentItem as any)?.fondo ??
    (parentItem as any)?.depthValue ??
    (parentItem as any)?.long ??
    parentItem.thickness ??
    (parentItem as any)?.profundidad ??
    null
  const numericDepth = Number(rawDepthCandidate)
  const catalogText = String(parentItem.catalogID || '')
  const catalogDepthMatch = catalogText.match(/-(\d{2,3})$/)
  const catalogDepthCm = catalogDepthMatch ? Number(catalogDepthMatch[1]) : 0
  const catalogDepthMm = catalogDepthCm ? catalogDepthCm * 10 : 0
  const depthMmFromNumeric =
    Number.isFinite(numericDepth) && numericDepth > 0
      ? numericDepth <= 300
        ? numericDepth * 10
        : numericDepth
      : 0
  const depthMm = depthMmFromNumeric > 0 ? depthMmFromNumeric : catalogDepthMm
  if (!Number.isFinite(depthMm) || depthMm <= 0) return baseCode
  if (depthMm > 1000) return `${baseCode}-105`
  if (catalogDepthCm === 100 || catalogText.includes('-100')) return `${baseCode}-100`
  return baseCode
}

const collectArmsForGroup = (
  allItems: SceneItem[],
  groupUnitIds: Set<string>,
  exteriorLips: Array<{ unit: SceneItem; side: 1 | -1 }>,
) => {
  const lipKeys = new Set(exteriorLips.map((lip) => `${String(lip.unit.id)}:${lip.side}`))

  return allItems.filter((item) => {
    if (!isArmLinkedItem(item)) return false
    if (groupUnitIds.has(String(item.firstParentID || ''))) return true

    const parent = item.firstParentID
      ? allItems.find((candidate) => String(candidate.id) === String(item.firstParentID))
      : undefined
    if (!parent?.matrix || !item.matrix) return false

    const side = getArmSideSign(parent, item)
    return lipKeys.has(`${String(parent.id)}:${side}`)
  })
}

const applyArmPlacement = (
  ctx: RealignContext,
  armId: AnyID,
  targetParent: SceneItem,
  liveArm: SceneItem,
  exteriorSideSign: 1 | -1,
  extraDistance: number,
  depth: number,
) => {
  const approachMatrix = buildApproachMatrixForArm(
    targetParent,
    liveArm,
    exteriorSideSign,
    extraDistance,
  )
  if (!approachMatrix) return false

  const pos = new Vector3()
  approachMatrix.decompose(pos, new Quaternion(), new Vector3())
  const beforePos = formatVec(getWorldPosFromItem(liveArm))
  const previousParentId = liveArm.firstParentID

  // Limpiar ocupación fantasma del padre anterior (brazo ≠ vecino de fila).
  if (previousParentId && String(previousParentId) !== String(targetParent.id)) {
    const previousParent = ctx.getItem(previousParentId as AnyID) as
      | (SceneItem & { snappedSides?: Partial<Record<'left' | 'right', string>> })
      | undefined
    clearArmFromUnitSnappedSides(previousParent, armId)
  }
  clearArmFromUnitSnappedSides(
    targetParent as SceneItem & { snappedSides?: Partial<Record<'left' | 'right', string>> },
    armId,
  )

  ctx.editItem(armId, {
    matrix: approachMatrix,
    x: pos.x,
    y: pos.y,
    z: pos.z,
    firstParentID: targetParent.id,
    depth,
  })

  // Settle dedicado (forcedSide): une el brazo al extremo de la nueva unidad.
  const forcedSide: 'left' | 'right' = exteriorSideSign < 0 ? 'left' : 'right'
  ctx.collideItems(armId, targetParent.id as AnyID, { forcedSide })

  const afterCollideArm = ctx.getItem(armId)
  const afterCollideParent = ctx.getItem(targetParent.id as AnyID)
  if (afterCollideArm && afterCollideParent) {
    const rotationMatrix = syncArmRotationToParent(afterCollideArm, afterCollideParent)
    if (rotationMatrix) {
      const rotPos = decomposeMatrixPosition(rotationMatrix)
      ctx.editItem(armId, {
        matrix: rotationMatrix,
        x: rotPos.x,
        y: rotPos.y,
        z: rotPos.z,
        firstParentID: targetParent.id,
        depth,
      })
    }
  }

  const afterArm = ctx.getItem(armId)
  logRealign('applyArmPlacement', {
    armId,
    targetParentId: targetParent.id,
    targetParentCatalogID: targetParent.catalogID,
    exteriorSideSign,
    extraDistance,
    beforePos,
    approachPos: formatVec(pos),
    afterPos: afterArm ? formatVec(getWorldPosFromItem(afterArm)) : null,
    afterParentId: afterArm?.firstParentID ?? null,
  })
  return true
}

export async function realignExteriorArmsAfterUnitDraw(
  newUnitId: AnyID,
  ctx: RealignContext,
): Promise<void> {
  logRealign('start', { newUnitId })

  const newUnit = ctx.getItem(newUnitId)
  if (!isBaseLinkedUnit(newUnit) || !newUnit?.matrix) {
    logRealign('skip:not-base-linked-unit', {
      newUnitId,
      unit: summarizeUnit(newUnit),
    })
    return
  }

  const itemsMap = new Map<AnyID, SceneItem>()
  for (const item of ctx.listItems()) {
    itemsMap.set(item.id as AnyID, item)
  }

  const rowCtx = createArmRowContext(itemsMap, newUnit, { getArmSideSign })
  const groupUnits = rowCtx.row as SceneItem[]
  if (groupUnits.length < 2) {
    logRealign('skip:group-too-small', {
      newUnitId,
      newUnit: summarizeUnit(newUnit),
      groupSize: groupUnits.length,
      groupUnits: groupUnits.map((unit) => summarizeUnit(unit)),
    })
    return
  }

  const newUnitInGroup = groupUnits.some((unit) => String(unit.id) === String(newUnitId))
  if (!newUnitInGroup) {
    logRealign('skip:new-unit-not-in-group', {
      newUnitId,
      newUnit: summarizeUnit(newUnit),
      groupUnits: groupUnits.map((unit) => summarizeUnit(unit)),
    })
    return
  }

  const exteriorLips = rowCtx.listExteriorLips()
  if (!exteriorLips.length) {
    logRealign('skip:no-exterior-lips', {
      newUnitId,
      groupUnits: groupUnits.map((unit) => summarizeUnit(unit)),
    })
    return
  }

  const groupUnitIds = new Set(groupUnits.map((unit) => String(unit.id)))
  const allSceneItems = Array.from(ctx.listItems())
  const arms = collectArmsForGroup(allSceneItems, groupUnitIds, exteriorLips)

  logRealign('group-ready', {
    newUnit: summarizeUnit(newUnit),
    groupUnits: groupUnits.map((unit) => summarizeUnit(unit)),
    exteriorLips: exteriorLips.map((lip) => ({
      unitId: lip.unit.id,
      catalogID: lip.unit.catalogID,
      side: lip.side,
    })),
    arms: arms.map((arm) => summarizeArm(arm, ctx.getItem(arm.firstParentID as AnyID))),
  })

  if (!arms.length) {
    logRealign('skip:no-arms-found', {
      newUnitId,
      groupUnitIds: Array.from(groupUnitIds),
    })
    return
  }

  // Corner / oreja / rinconero / RELAX: no hospedan BR independiente → purgar, no migrar.
  const newUnitIsCornerLike =
    isRinconeroCatalog(newUnit.catalogID) ||
    isOreja(newUnit.catalogID) ||
    isModularCornerSnapCatalog(newUnit.catalogID)
  const newUnitHostsArms =
    !newUnitIsCornerLike && unitCanHostIndependentArm(newUnit.catalogID)
  const claimedLipKeys = new Set<string>()

  // Reservar labios ya ocupados por brazos que se quedan en su padre.
  for (const arm of arms) {
    const parent = ctx.getItem(arm.firstParentID as AnyID)
    if (!parent?.matrix || !arm.matrix) continue
    const side = resolveArmFlushSide(parent, arm)
    const stillExterior = exteriorLips.some(
      (lip) => String(lip.unit.id) === String(parent.id) && lip.side === side,
    )
    if (stillExterior) {
      claimedLipKeys.add(`${String(parent.id)}:${side}`)
    }
  }

  for (const arm of arms) {
    const parent = ctx.getItem(arm.firstParentID as AnyID)
    if (!parent?.matrix) {
      logRealign('skip:arm-parent-missing', {
        arm: summarizeArm(arm, parent),
      })
      continue
    }

    const parentSide = resolveArmFlushSide(parent, arm)
    const parentStillExterior = exteriorLips.some(
      (lip) => String(lip.unit.id) === String(parent.id) && lip.side === parentSide,
    )
    const sandwiched =
      !newUnitHostsArms && isArmSandwichedBetweenParentAndNewUnit(arm, parent, newUnit)

    // Unidad que no admite BR (RELAX / Corner / oreja): purgar labio ocupado o BR atrapado.
    if (!newUnitHostsArms && (!parentStillExterior || sandwiched)) {
      if (ctx.removeItem) {
        logRealign('purge:new-unit-cannot-host-arm', {
          arm: summarizeArm(arm, parent),
          newUnit: summarizeUnit(newUnit),
          parentStillExterior,
          sandwiched,
        })
        clearArmFromUnitSnappedSides(
          parent as SceneItem & { snappedSides?: Partial<Record<'left' | 'right', string>> },
          arm.id,
        )
        ctx.removeItem(arm.id as AnyID)
        // Solo RELAX: cerrar hueco tras borrar BR. Corner/oreja no se tocan.
        if (isRelaxIntegratedUnitByDefaults(newUnit.catalogID)) {
          const forcedSide: 'left' | 'right' = parentSide === 1 ? 'left' : 'right'
          try {
            ctx.collideItems(newUnitId, parent.id as AnyID, { forcedSide })
            logRealign('relax-resnap-after-arm-purge', {
              newUnitId: String(newUnitId),
              parentId: String(parent.id),
              forcedSide,
            })
          } catch (error) {
            console.warn(
              '[realignExteriorArmsAfterUnitDraw] RELAX resnap tras purgar BR falló:',
              error,
            )
          }
        }
      }
      continue
    }

    if (parentStillExterior) {
      logRealign('keep:parent-still-exterior', {
        arm: summarizeArm(arm, parent),
        parentSide,
      })
      continue
    }

    if (!newUnitHostsArms) {
      if (ctx.removeItem) {
        logRealign('purge:new-unit-cannot-host-arm-fallback', {
          arm: summarizeArm(arm, parent),
          newUnit: summarizeUnit(newUnit),
        })
        clearArmFromUnitSnappedSides(
          parent as SceneItem & { snappedSides?: Partial<Record<'left' | 'right', string>> },
          arm.id,
        )
        ctx.removeItem(arm.id as AnyID)
        if (isRelaxIntegratedUnitByDefaults(newUnit.catalogID)) {
          const forcedSide: 'left' | 'right' = parentSide === 1 ? 'left' : 'right'
          try {
            ctx.collideItems(newUnitId, parent.id as AnyID, { forcedSide })
          } catch {
            /* noop */
          }
        }
      }
      continue
    }

    const lip = resolveExteriorLipForArm(itemsMap, arm, newUnit, getArmSideSign, {
      claimedLipKeys,
    })
    const targetParent = lip?.unit as SceneItem | undefined
    const exteriorSideSign = lip?.side
    if (!targetParent?.id || !targetParent.matrix || exteriorSideSign == null) {
      if (ctx.removeItem) {
        logRealign('purge:no-free-exterior-lip', {
          arm: summarizeArm(arm, parent),
        })
        clearArmFromUnitSnappedSides(
          parent as SceneItem & { snappedSides?: Partial<Record<'left' | 'right', string>> },
          arm.id,
        )
        ctx.removeItem(arm.id as AnyID)
      } else {
        logRealign('skip:target-parent-missing', {
          arm: summarizeArm(arm, parent),
          lip,
        })
      }
      continue
    }

    if (!unitCanHostIndependentArm(targetParent.catalogID)) {
      if (ctx.removeItem) {
        logRealign('purge:target-cannot-host-arm', {
          arm: summarizeArm(arm, parent),
          targetParent: summarizeUnit(targetParent),
        })
        ctx.removeItem(arm.id as AnyID)
      }
      continue
    }

    const lipKey = `${String(targetParent.id)}:${exteriorSideSign}`
    if (claimedLipKeys.has(lipKey)) {
      if (ctx.removeItem) {
        logRealign('purge:lip-already-claimed', {
          arm: summarizeArm(arm, parent),
          lipKey,
        })
        ctx.removeItem(arm.id as AnyID)
      }
      continue
    }

    logRealign('arm-target', {
      arm: summarizeArm(arm, parent),
      targetParent: summarizeUnit(targetParent),
      exteriorSideSign,
    })

    const oldCatalogUpper = String(arm.catalogID || '').toUpperCase()
    const armBaseMatch = oldCatalogUpper.match(/^BR\d+/i)
    const armBaseCode = armBaseMatch ? armBaseMatch[0] : oldCatalogUpper
    const desiredCatalog = resolveArmCatalogByParentDepth(armBaseCode, targetParent)

    let effectiveArmId: AnyID = arm.id
    if (desiredCatalog.toUpperCase() !== oldCatalogUpper) {
      try {
        effectiveArmId = await ctx.replaceItem(
          String(arm.id),
          desiredCatalog,
          String(arm.icono || ''),
          'items',
        )
        logRealign('arm-catalog-replaced', {
          oldArmId: arm.id,
          effectiveArmId,
          oldCatalog: oldCatalogUpper,
          desiredCatalog,
        })
      } catch (error) {
        console.warn(
          '[realignExteriorArmsAfterUnitDraw] No se pudo cambiar catálogo de brazo:',
          error,
        )
        logRealign('arm-catalog-replace-failed', {
          armId: arm.id,
          oldCatalog: oldCatalogUpper,
          desiredCatalog,
          error: error instanceof Error ? error.message : String(error),
        })
      }
    }

    const liveArm = ctx.getItem(effectiveArmId)
    const liveParent = ctx.getItem(targetParent.id as AnyID)
    if (!liveArm?.matrix || !liveParent?.matrix) {
      logRealign('skip:live-arm-or-parent-missing', {
        effectiveArmId,
        liveArm: summarizeArm(liveArm, liveParent),
        targetParent: summarizeUnit(liveParent),
      })
      continue
    }

    const placed = applyArmPlacement(
      ctx,
      effectiveArmId,
      liveParent,
      liveArm,
      exteriorSideSign,
      0,
      Number(arm.depth || 1),
    )
    if (placed) {
      claimedLipKeys.add(lipKey)
    }

    const armAfter = ctx.getItem(effectiveArmId)
    const parentAfter = ctx.getItem(targetParent.id as AnyID)
    const finalSign =
      armAfter?.matrix && parentAfter?.matrix
        ? getArmSideSign(parentAfter, armAfter)
        : null

    logRealign('arm-finished', {
      effectiveArmId,
      placed,
      expectedSideSign: exteriorSideSign,
      finalSideSign: finalSign,
      migratedToNewUnit: String(targetParent.id) === String(newUnitId),
      finalArm: summarizeArm(armAfter, parentAfter),
    })
  }
}
